import {
    addEventProcessor,
    captureException,
    captureFeedback,
    Event,
    EventHint,
    getActiveSpan,
    getClient,
    getCurrentScope,
    lastEventId,
    setTag,
} from '@sentry/react';
import { v4 } from 'uuid';
import AppConfig from './appconfig';
import { ConsoleEntry, getConsoleEntries } from './console-buffer';
import { redactDicomUids } from './phiRedaction';

/**
 * Diagnostics shown on the unexpected error page so that a screenshot or a
 * copied report is enough to find the matching Sentry event and backend logs.
 *
 * Deliberately excludes the full URL: workflow routes embed the patient ID.
 * DICOM UIDs are redacted, as in Sentry events: the report is copied and
 * e-mailed outside the browser.
 */
export interface ErrorDiagnostics {
    /** Sentry event ID of the captured exception; absent if Sentry is off. */
    eventId: string | undefined;
    /** Sentry trace ID; links the error to the page's HTTP spans. */
    traceId: string | undefined;
    /** `sessionId` query parameter sent with every backend request. */
    backendSessionId: string | undefined;
    occurredAt: string;
    appVersion: string;
    /** First path segment only, e.g. `contouring`. */
    page: string;
    userAgent: string;
    errorName: string;
    errorMessage: string;
    stack: string | undefined;
    componentStack: string | undefined;
    /** Console output before the error, oldest first, already redacted. */
    consoleEntries: ConsoleEntry[];
}

const MAILTO_STACK_MAX_LENGTH = 1200;

let currentBackendSessionId: string | undefined;

/**
 * Remembers the backend session ID for the error page and tags all Sentry
 * events with it, so backend logs can be matched to frontend errors.
 */
export function recordBackendSessionId(sessionId: string): void {
    currentBackendSessionId = sessionId;
    setTag('backend.session_id', sessionId);
}

export function getBackendSessionId(): string | undefined {
    return currentBackendSessionId;
}

export function isSentryEnabled(): boolean {
    return Boolean(getClient()?.getDsn());
}

// Event ID under which Sentry stored each error object, whoever captured it.
// A render crash usually reaches Sentry first through captureConsole (React
// logs caught errors with console.error) or the Sentry ErrorBoundary; a later
// captureException of the same object is dropped as a duplicate but still
// returns a fresh ID that no stored event has. Registered on the isolation
// scope at import time, so it also sees events captured before Sentry.init.
const storedEventIds = new WeakMap<object, string>();

function rememberStoredEventId(event: Event, hint: EventHint): Event {
    const original = hint.originalException;
    if (
        typeof original === 'object' &&
        original !== null &&
        event.event_id &&
        !storedEventIds.has(original)
    ) {
        storedEventIds.set(original, event.event_id);
    }
    return event;
}
addEventProcessor(rememberStoredEventId);

function isAlreadyCaptured(error: object): boolean {
    return Boolean(
        (error as { __sentry_captured__?: boolean }).__sentry_captured__
    );
}

/**
 * Makes sure the error is in Sentry and returns the event ID it is stored
 * under, so the ID shown to the user can be looked up. Captures the error
 * only if nothing has captured it yet.
 */
export function captureErrorOnce(
    error: unknown,
    boundary: string,
    knownEventId?: string
): string | undefined {
    if (!isSentryEnabled()) {
        return undefined;
    }
    const isObject = typeof error === 'object' && error !== null;
    if (isObject) {
        const storedEventId = storedEventIds.get(error);
        if (storedEventId) {
            return storedEventId;
        }
        if (isAlreadyCaptured(error)) {
            // Captured before the processor saw it; best available guess.
            return lastEventId() ?? knownEventId;
        }
    }
    if (knownEventId) {
        return knownEventId;
    }
    const eventId = captureException(error, {
        tags: { 'error.boundary': boundary },
        level: 'fatal',
    });
    if (!isObject) {
        return eventId;
    }
    if (!storedEventIds.has(error)) {
        // Not processed, e.g. sampled out; still never capture it twice.
        storedEventIds.set(error, eventId);
    }
    return storedEventIds.get(error);
}

const FEEDBACK_SEND_TIMEOUT_MS = 5000;

type FeedbackParams = Parameters<typeof captureFeedback>[0];

/**
 * Sends user feedback and resolves `true` only when Sentry accepted it
 * (2xx). `flush()` is not enough: it also resolves `true` after a 429 or a
 * network failure, as it only reports that the send queue drained.
 */
export function sendFeedbackConfirmed(
    params: FeedbackParams,
    timeoutMs: number = FEEDBACK_SEND_TIMEOUT_MS
): Promise<boolean> {
    const client = getClient();
    if (!client) {
        return Promise.resolve(false);
    }
    // Chosen up front so the hook below can recognise the feedback event.
    const feedbackEventId = v4().replaceAll('-', '');
    return new Promise((resolve) => {
        const finish = (accepted: boolean) => {
            clearTimeout(timer);
            unsubscribe();
            resolve(accepted);
        };
        // Covers events that are never sent, e.g. dropped by beforeSend.
        const timer = setTimeout(() => finish(false), timeoutMs);
        const unsubscribe = client.on(
            'afterSendEvent',
            (event, sendResponse) => {
                if (event.event_id === feedbackEventId) {
                    const status = sendResponse.statusCode;
                    finish(
                        status !== undefined && status >= 200 && status < 300
                    );
                }
            }
        );
        captureFeedback(params, { event_id: feedbackEventId });
    });
}

function currentTraceId(): string | undefined {
    const span = getActiveSpan();
    if (span) {
        return span.spanContext().traceId;
    }
    return getCurrentScope().getPropagationContext().traceId || undefined;
}

function describeError(error: unknown): {
    name: string;
    message: string;
    stack: string | undefined;
} {
    if (error instanceof Error) {
        return {
            name: error.name || 'Error',
            message: error.message || '(no message)',
            stack: error.stack,
        };
    }
    if (typeof error === 'string') {
        return { name: 'Error', message: error, stack: undefined };
    }
    let message: string;
    try {
        message = JSON.stringify(error) ?? String(error);
    } catch {
        message = String(error);
    }
    return { name: 'Non-Error thrown', message, stack: undefined };
}

export function collectErrorDiagnostics(
    error: unknown,
    options: {
        eventId: string | undefined;
        componentStack?: string;
        now?: Date;
    }
): ErrorDiagnostics {
    const { name, message, stack } = redactDicomUids(describeError(error));
    return {
        eventId: options.eventId,
        traceId: isSentryEnabled() ? currentTraceId() : undefined,
        backendSessionId: currentBackendSessionId,
        occurredAt: (options.now ?? new Date()).toISOString(),
        appVersion: AppConfig.version,
        page: window.location.pathname.split('/')[1] || '/',
        userAgent: navigator.userAgent,
        errorName: name,
        errorMessage: message,
        stack,
        componentStack:
            redactDicomUids(options.componentStack?.trim()) || undefined,
        consoleEntries: getConsoleEntries(),
    };
}

/** One console entry as `HH:MM:SS.mmm LEVEL message`. */
export function formatConsoleEntry(entry: ConsoleEntry): string {
    return `${entry.time.slice(11, 23)} ${entry.level.toUpperCase().padEnd(5)} ${entry.message}`;
}

/** The `at ...` lines of a stack trace, without the leading message line. */
export function stackFrames(stack: string | undefined): string[] {
    if (!stack) {
        return [];
    }
    return stack
        .split('\n')
        .map((line) => line.trim())
        .filter((line) => line.startsWith('at ') || line.includes('@'));
}

/**
 * Text with URLs on the given origin made relative, e.g.
 * `at f (https://demo.mvision.ai/assets/index.js:1:2)` becomes
 * `at f (/assets/index.js:1:2)`. URLs on other origins are unchanged.
 */
export function withoutOrigin(text: string, origin: string): string {
    return origin ? text.split(`${origin}/`).join('/') : text;
}

/** Plain-text report for the clipboard and support e-mails. */
export function formatDiagnosticsReport(
    diagnostics: ErrorDiagnostics,
    stackMaxLength: number = Number.POSITIVE_INFINITY
): string {
    const notAvailable = 'n/a';
    const stack = diagnostics.stack ?? 'No stack trace available';
    const truncatedStack =
        stack.length > stackMaxLength
            ? `${stack.slice(0, stackMaxLength)}\n... (truncated)`
            : stack;
    const lines = [
        'Workspace+ error report',
        `Error ID: ${diagnostics.eventId ?? notAvailable}`,
        `Time (UTC): ${diagnostics.occurredAt}`,
        `Version: ${diagnostics.appVersion}`,
        `Session ID: ${diagnostics.backendSessionId ?? notAvailable}`,
        `Trace ID: ${diagnostics.traceId ?? notAvailable}`,
        `Page: ${diagnostics.page}`,
        `Browser: ${diagnostics.userAgent}`,
        '',
        `${diagnostics.errorName}: ${diagnostics.errorMessage}`,
        '',
        'Stack trace:',
        truncatedStack,
    ];
    if (diagnostics.componentStack) {
        lines.push('', 'Component stack:', diagnostics.componentStack);
    }
    if (diagnostics.consoleEntries.length > 0) {
        lines.push(
            '',
            'Console (UTC):',
            ...diagnostics.consoleEntries.map(formatConsoleEntry)
        );
    }
    return lines.join('\n');
}

export function supportMailtoHref(
    supportEmail: string,
    diagnostics: ErrorDiagnostics
): string {
    const subject = `Workspace+ error ${diagnostics.eventId ?? ''}`.trim();
    const body = [
        'Please describe what you were doing when the error occurred',
        '(do not include patient names or identifiers):',
        '',
        '',
        '',
        formatDiagnosticsReport(
            // A mail client truncates long mailto links; Copy Details has
            // the full report.
            { ...diagnostics, componentStack: undefined, consoleEntries: [] },
            MAILTO_STACK_MAX_LENGTH
        ),
    ].join('\n');
    return `mailto:${supportEmail}?subject=${encodeURIComponent(
        subject
    )}&body=${encodeURIComponent(body)}`;
}
